import React, { useState, useEffect, useContext } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { API_URL } from '../constants';
import { AuthContext } from '../context/AuthContext';
import { TechnicalSpec as TechnicalSpecType } from '../types/technical-sheet';

interface TechnicalSpec {
  key?: string;
  value?: string;
  category?: string;
  dynamicSizeTable?: {
    columns: string[];
    rows: string[];
    data: Record<string, Record<string, string>>;
  };
  fabricSpecs: Array<{
    composition: string;
    weight: string;
    construction: string;
    supplier: string;
    colorFastness: string;
    shrinkage: string;
  }>;
  stitchDetails: Array<{
    location: string;
    stitchType: string;
    threadType: string;
    stitchesPerInch: string;
    notes?: string;
  }>;
  printingDetails: Array<{
    technique: string;
    location: string;
    colors: string;
    size?: string;
    notes?: string;
  }>;
  careInstructions: string[];
  qualityStandards: string[];
}

interface Material {
  name: string;
  percentage: number;
}

interface Measurement {
  size: string;
  chest?: number;
  waist?: number;
  hip?: number;
  inseam?: number;
  sleeve?: number;
  [key: string]: any;
}

interface ProductData {
  _id: string;
  modelName: string;
  productCode?: string;
  prototypeCode?: string;
  description?: string;
  modelType: string;
  fabric: string;
  color: {
    name: string;
    firmCode: string;
    colorCode?: string;
    hexCode?: string;
  };
  technicalSpecs?: TechnicalSpec[];
  materials?: Material[];
  measurements?: Measurement[];
  washInstructions?: string[];
  notes?: string;
  imageUrls?: string[];
  modelImage?: string;
  isPrototype: boolean;
  status: string;
  createdAt: string;
  updatedAt: string;
  technicalSpec?: TechnicalSpec;
}

const TechnicalSheetDetail: React.FC = () => {
  const { productId } = useParams<{ productId: string }>();
  const [product, setProduct] = useState<ProductData | null>(null);
  const [fabric, setFabric] = useState<{ name: string; type: string } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState<'specs'|'measurements'>('specs');
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [isPdfPreviewOpen, setIsPdfPreviewOpen] = useState(false);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const navigate = useNavigate();
  const { hasPermission, isAdmin } = useContext(AuthContext);

  const canEditTechnicalSheet = isAdmin || hasPermission('designer', 'update');

    const fetchProductData = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${API_URL}/products/${productId}`);
        
        if (!response.ok) {
          throw new Error('Ürün bilgileri yüklenemedi');
        }
        
        const data = await response.json();
        setProduct(data);
      
      // Kumaş bilgisini çek
      if (data.fabric) {
        try {
          const fabricResponse = await fetch(`${API_URL}/fabrics/${data.fabric}`);
          if (fabricResponse.ok) {
            const fabricData = await fabricResponse.json();
            setFabric(fabricData);
          }
        } catch (err) {
          console.error('Kumaş bilgisi yüklenirken hata:', err);
        }
      }
        
        // İlk görseli seç, varsa
        if (data.imageUrls && data.imageUrls.length > 0) {
          setSelectedImage(data.imageUrls[0]);
        } else if (data.modelImage) {
          setSelectedImage(data.modelImage);
        }
        
        setLoading(false);
      } catch (err) {
        setError('Ürün bilgileri yüklenemedi.');
        setLoading(false);
        console.error('Ürün bilgileri yüklenirken hata oluştu:', err);
      }
    };

  useEffect(() => {
    if (productId) {
      fetchProductData();
    }
  }, [productId]);

  // Teknik özellikleri kategoriye göre grupla
  const groupedSpecs = product?.technicalSpecs?.reduce((acc: Record<string, TechnicalSpec[]>, spec) => {
    const category = spec.category || 'Genel';
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(spec);
    return acc;
  }, {} as Record<string, TechnicalSpec[]>) || {};

  // Ürün tipine uygun ikon
  const getProductTypeIcon = () => {
    if (product?.modelType === 'knitting') {
      return (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-indigo-600" viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M3 5a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 5a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 5a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
        </svg>
      );
    } else {
      return (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-blue-600" viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clipRule="evenodd" />
        </svg>
      );
    }
  };

  const handlePDFDownload = async () => {
    try {
      const response = await fetch(`${API_URL}/pdf/generate`, {
        method: 'POST',
        headers: {
          'Accept': 'application/pdf',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          product,
          technicalSpec: product.technicalSpec
        })
      });

      if (!response.ok) {
        throw new Error('PDF oluşturulamadı');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `technical-sheet-${productId}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
    } catch (error) {
      console.error('PDF indirme hatası:', error);
      alert('PDF indirilemedi');
    }
  };

  const handlePdfPreview = async () => {
    try {
      const response = await fetch(`${API_URL}/pdf/generate`, {
        method: 'POST',
        headers: {
          'Accept': 'application/pdf',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ productId }) // Sadece productId gönder
      });

      if (!response.ok) {
        throw new Error('PDF oluşturulamadı');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      setPdfUrl(url);
      setIsPdfPreviewOpen(true);
    } catch (error) {
      console.error('PDF önizleme hatası:', error);
      alert('PDF önizleme yüklenemedi');
    }
  };

  const PdfPreviewModal = () => {
    if (!isPdfPreviewOpen || !pdfUrl) return null;

    return (
      <div className="fixed inset-0 z-50 overflow-y-auto bg-black bg-opacity-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl h-[90vh] flex flex-col">
          <div className="flex justify-between items-center p-4 border-b">
            <h3 className="text-lg font-semibold text-gray-900">PDF Önizleme</h3>
            <button
              onClick={() => {
                setIsPdfPreviewOpen(false);
                window.URL.revokeObjectURL(pdfUrl);
                setPdfUrl(null);
              }}
              className="text-gray-500 hover:text-gray-700"
            >
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <div className="flex-1 p-1 bg-gray-100">
            <iframe
              src={pdfUrl}
              className="w-full h-full rounded border-none"
              title="PDF Önizleme"
            />
          </div>
        </div>
      </div>
    );
  };

  const renderTechnicalSpecs = () => {
    if (!product?.technicalSpec) return null;
    const { dynamicSizeTable, fabricSpecs, stitchDetails, printingDetails, careInstructions, qualityStandards } = product.technicalSpec;

    // Eğer dynamicSizeTable yok ya da boşsa çık
    if (!dynamicSizeTable || !dynamicSizeTable.columns || !dynamicSizeTable.rows) {
      return <p className="text-center text-gray-500">Beden ölçüleri bulunamadı.</p>;
    }

    return (
      <div className="bg-white p-4 rounded-lg border shadow-sm overflow-x-auto">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Beden Ölçüleri</h3>
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-3 py-2 text-left font-medium text-gray-700">Beden</th>
              {dynamicSizeTable.columns.map(col => (
                <th key={col} className="px-3 py-2 text-left font-medium text-gray-700">
                  {col}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {dynamicSizeTable.rows.map(row => (
              <tr key={row}>
                <td className="px-3 py-2 font-medium text-gray-900">{row}</td>
                {dynamicSizeTable.columns.map(col => (
                  <td key={col} className="px-3 py-2 text-gray-600">
                    {dynamicSizeTable.data[row]?.[col] || '-'}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  // Ölçü tablosunu render eder
  const renderMeasurements = () => {
    if (!product?.measurements || product.measurements.length === 0) {
      return <p className="text-center text-gray-500">Ölçü verisi bulunamadı.</p>;
    }
    // başlık alanları (size dışında)
    const fields = Object.keys(product.measurements[0]).filter(f => f !== 'size');
    return (
      <div className="bg-white p-4 rounded-lg border shadow-sm overflow-x-auto">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Ölçü Tablosu</h3>
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-3 py-2">Beden</th>
              {fields.map(f => (
                <th key={f} className="px-3 py-2 text-left">{f.charAt(0).toUpperCase()+f.slice(1)}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {product.measurements.map((m, i) => (
              <tr key={i}>
                <td className="px-3 py-2">{m.size}</td>
                {fields.map(f => (
                  <td key={f} className="px-3 py-2">{m[f] ?? '-'}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex justify-center items-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="min-h-screen flex justify-center items-center">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-red-600 mb-2">Hata</h2>
          <p>{error || 'Ürün bulunamadı.'}</p>
          <Link to="/technical-sheet" className="mt-4 inline-block text-blue-600 hover:underline">
            Teknik Föy Listesine Dön
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-6xl">
      {/* Üst bilgi ve butonlar */}
      <div className="mb-6 flex justify-between items-center">
        <div className="flex items-center space-x-2">
          <Link to="/technical-sheet" className="flex items-center text-blue-600 hover:text-blue-800 p-2">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5 mr-1"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            <span>Listeye Dön</span>
          </Link>
          
          <div className="h-6 w-px bg-gray-300"></div>
          
          <div className="text-sm text-gray-600 font-medium flex items-center">
            <span className="mr-2">Versiyon:</span>
            <span className="bg-blue-100 text-blue-800 text-xs font-semibold px-2.5 py-0.5 rounded">1.0</span>
          </div>
        </div>
        
        <div className="flex space-x-3">
          <button
            onClick={() => window.print()}
            className="px-3 py-2 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 flex items-center text-sm"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-4 w-4 mr-1"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
            </svg>
            Yazdır
          </button>
          
          <button
            onClick={handlePdfPreview}
            className="px-3 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700 flex items-center text-sm"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-4 w-4 mr-1"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
            </svg>
            Önizle
          </button>
          
          <button
            onClick={handlePDFDownload}
            className="px-3 py-2 bg-red-600 text-white rounded hover:bg-red-700 flex items-center text-sm"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-4 w-4 mr-1"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
            PDF İndir
          </button>
          
          {canEditTechnicalSheet && (
            <button
              onClick={() => navigate(`/technical-sheet/${productId}/edit`)}
              className="px-3 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center text-sm"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-4 w-4 mr-1"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
              </svg>
              Düzenle
            </button>
          )}
        </div>
      </div>
      
      {/* Sekmeler */}
      <div className="mb-6 flex space-x-4">
        <button
          onClick={() => setActiveTab('specs')}
          className={`px-4 py-2 rounded ${activeTab==='specs'? 'bg-indigo-600 text-white':'bg-gray-100 text-gray-700'}`}
        >Özellikler</button>
        <button
          onClick={() => setActiveTab('measurements')}
          className={`px-4 py-2 rounded ${activeTab==='measurements'? 'bg-indigo-600 text-white':'bg-gray-100 text-gray-700'}`}
        >Ölçümler</button>
      </div>

      {/* Teknik Föy Ana Kart */}
      <div className="bg-white rounded-lg shadow-lg overflow-hidden border border-gray-100">
        {/* Başlık ve temel bilgiler */}
        <div className="bg-gradient-to-r from-gray-50 to-gray-100 p-6 border-b relative">
          {/* Durum etiketi */}
          <div className="absolute top-4 right-4">
            <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
              product.status === 'active' ? 'bg-green-100 text-green-800' : 
              product.status === 'archived' ? 'bg-gray-100 text-gray-800' : 
              'bg-yellow-100 text-yellow-800'}`}
            >
              {product.status === 'active' ? 'Aktif' : 
               product.status === 'archived' ? 'Arşivlendi' : 'Taslak'}
            </span>
          </div>
          
          <div className="flex items-center mb-2">
            {getProductTypeIcon()}
            <h1 className="text-2xl font-bold text-gray-800 ml-2">{product.modelName}</h1>
          </div>
          
          {/* Temel bilgi kartları */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
            <div className="bg-white p-3 rounded-lg border shadow-sm">
              <div className="text-xs text-gray-500 uppercase font-semibold tracking-wider mb-1">Ürün Kodu</div>
              <div className="font-medium">{product.isPrototype ? product.prototypeCode : product.productCode || 'Belirtilmemiş'}</div>
            </div>
            
            <div className="bg-white p-3 rounded-lg border shadow-sm">
              <div className="text-xs text-gray-500 uppercase font-semibold tracking-wider mb-1">Kumaş Tipi</div>
              <div className="font-medium">{product.modelType === 'knitting' ? 'Örme' : 'Dokuma'} / {fabric?.name || 'Yükleniyor...'}</div>
            </div>
            
            <div className="bg-white p-3 rounded-lg border shadow-sm flex items-center">
              <div className="flex-grow">
                <div className="text-xs text-gray-500 uppercase font-semibold tracking-wider mb-1">Renk</div>
                <div className="font-medium">{product.color?.name || 'Belirtilmemiş'}</div>
                <div className="text-xs text-gray-500">{product.color?.firmCode || 'Kod yok'}</div>
              </div>
              {product.color?.hexCode && (
                <div 
                  className="w-8 h-8 rounded-full border border-gray-200" 
                  style={{ backgroundColor: product.color.hexCode }}
                ></div>
              )}
            </div>
          </div>
        </div>
        
        <div className="p-6">
          {/* İki sütunlu layout */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            {/* Sol sütun - Ürün görseli ve açıklama */}
            <div className="lg:col-span-5">
              {/* Ana görsel */}
              {selectedImage ? (
                <div className="bg-gray-50 rounded-lg overflow-hidden shadow-sm border mb-4 flex items-center justify-center" style={{height: '360px'}}>
                  <img
                    src={`${API_URL}${selectedImage}`}
                    alt={product.modelName}
                    className="max-w-full max-h-full object-contain"
                  />
                </div>
              ) : (
                <div className="bg-gray-50 rounded-lg flex items-center justify-center h-64 shadow-sm border mb-4">
                  <p className="text-gray-400">Görsel yok</p>
                </div>
              )}
              
              {/* Thumbnail galerisi */}
              {product.imageUrls && product.imageUrls.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-6">
                  {product.imageUrls.map((url, index) => (
                    <button
                      key={index}
                      onClick={() => setSelectedImage(url)}
                      className={`bg-gray-50 rounded overflow-hidden w-16 h-16 flex items-center justify-center border ${selectedImage === url ? 'border-blue-500 ring-2 ring-blue-200' : 'border-gray-200'}`}
                    >
                      <img
                        src={`${API_URL}${url}`}
                        alt={`${product.modelName} - ${index + 1}`}
                        className="max-w-full max-h-full object-contain"
                      />
                    </button>
                  ))}
                  {product.modelImage && product.imageUrls.indexOf(product.modelImage) === -1 && (
                    <button
                      onClick={() => setSelectedImage(product.modelImage)}
                      className={`bg-gray-50 rounded overflow-hidden w-16 h-16 flex items-center justify-center border ${selectedImage === product.modelImage ? 'border-blue-500 ring-2 ring-blue-200' : 'border-gray-200'}`}
                    >
                      <img
                        src={`${API_URL}${product.modelImage}`}
                        alt={`${product.modelName} - model`}
                        className="max-w-full max-h-full object-contain"
                      />
                    </button>
                  )}
                </div>
              )}
              
              {/* Ürün açıklaması */}
              {product.description && (
                <div className="mb-6 bg-gray-50 p-4 rounded-lg border">
                  <h2 className="text-md font-semibold text-gray-700 mb-2">Ürün Açıklaması</h2>
                  <p className="text-gray-600 text-sm">{product.description}</p>
                </div>
              )}
              
              {/* Notlar */}
              {product.notes && (
                <div className="mb-6">
                  <h2 className="text-md font-semibold text-gray-700 mb-2">Notlar</h2>
                  <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg text-sm text-gray-700">
                    <p>{product.notes}</p>
                  </div>
                </div>
              )}
            </div>
            
            {/* Sağ sütun - Teknik detaylar */}
            <div className="lg:col-span-7">
              {activeTab==='specs' ? renderTechnicalSpecs() : renderMeasurements()}
            </div>
          </div>
          
          {/* Tarih ve versiyonlama bilgileri */}
          <div className="mt-8 pt-4 border-t border-gray-200">
            <div className="flex flex-wrap justify-between text-xs text-gray-500 items-center">
              <div className="flex items-center space-x-4">
                <p>Oluşturma: {new Date(product?.createdAt || '').toLocaleDateString('tr-TR')}</p>
                <p>Son Güncelleme: {new Date(product?.updatedAt || '').toLocaleDateString('tr-TR')}</p>
              </div>
              <div className="flex items-center space-x-2">
                <span className="text-xs text-gray-500">ID: {product?._id}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <PdfPreviewModal />
    </div>
  );
};

export default TechnicalSheetDetail;
